import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  DollarSign, ShoppingCart, Users, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle, Building, BarChart3, ArrowUpRight,
  ArrowDownRight, Calendar, RefreshCw, Loader2, ChevronRight,
  Target, AlertCircle, Info
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HEALTH_COLORS = {
  good: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-700' },
  warning: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-700' },
  critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700' }
};

export default function FranchiseFinancialDashboard() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [trends, setTrends] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeHistory, setStoreHistory] = useState(null);
  const [budgetAlerts, setBudgetAlerts] = useState(null);
  const [checkingBudgets, setCheckingBudgets] = useState(false);

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load dashboard summary
      const summaryRes = await fetch(`${API_URL}/api/store-financials/dashboard-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }

      // Load weekly report
      const reportRes = await fetch(`${API_URL}/api/store-financials/weekly-report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (reportRes.ok) {
        const data = await reportRes.json();
        setWeeklyReport(data);
      }

      // Load trends
      const trendsRes = await fetch(`${API_URL}/api/store-financials/trends?weeks=8`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (trendsRes.ok) {
        const data = await trendsRes.json();
        setTrends(data.trends.map(t => ({
          ...t,
          week: new Date(t.week_ending).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
        })));
      }

      // Load budget alerts summary
      const alertsRes = await fetch(`${API_URL}/api/store-financials/budget-alerts/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (alertsRes.ok) {
        const data = await alertsRes.json();
        setBudgetAlerts(data);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Error loading financial data');
    } finally {
      setLoading(false);
    }
  };

  const runBudgetCheck = async () => {
    setCheckingBudgets(true);
    try {
      const res = await fetch(`${API_URL}/api/store-financials/check-budget-alerts`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Budget check complete: ${data.alerts_created} alerts created`);
        // Reload alerts
        const alertsRes = await fetch(`${API_URL}/api/store-financials/budget-alerts/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (alertsRes.ok) {
          const alertsData = await alertsRes.json();
          setBudgetAlerts(alertsData);
        }
      } else {
        toast.error('Failed to run budget check');
      }
    } catch (err) {
      toast.error('Error running budget check');
    } finally {
      setCheckingBudgets(false);
    }
  };

  const loadStoreHistory = async (storeId) => {
    try {
      const res = await fetch(`${API_URL}/api/store-financials/store/${storeId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStoreHistory(data);
        setSelectedStore(storeId);
      }
    } catch (err) {
      toast.error('Error loading store history');
    }
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '$0';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value) => {
    if (!value) return '0%';
    return `${value > 0 ? '+' : ''}${value}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="franchise-financial-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Store Financials</h1>
          <p className="text-slate-500">
            Week ending {summary?.current_week ? new Date(summary.current_week).toLocaleDateString('en-AU', { 
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' 
            }) : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={runBudgetCheck} 
            variant="outline"
            disabled={checkingBudgets}
            data-testid="run-budget-check-btn"
          >
            {checkingBudgets ? (
              <Loader2 size={16} className="mr-2 animate-spin" />
            ) : (
              <Target size={16} className="mr-2" />
            )}
            Check Budgets
          </Button>
          <Button onClick={loadData} variant="outline">
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Budget Alerts Banner */}
      {budgetAlerts && budgetAlerts.total_active_alerts > 0 && (
        <Card className={`${budgetAlerts.critical_count > 0 ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300'}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className={`${budgetAlerts.critical_count > 0 ? 'text-red-600' : 'text-yellow-600'}`} size={24} />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <p className={`font-semibold ${budgetAlerts.critical_count > 0 ? 'text-red-800' : 'text-yellow-800'}`}>
                    {budgetAlerts.total_active_alerts} Active Budget Alert{budgetAlerts.total_active_alerts > 1 ? 's' : ''}
                  </p>
                  {budgetAlerts.critical_count > 0 && (
                    <Badge className="bg-red-200 text-red-800">{budgetAlerts.critical_count} Critical</Badge>
                  )}
                  {budgetAlerts.warning_count > 0 && (
                    <Badge className="bg-yellow-200 text-yellow-800">{budgetAlerts.warning_count} Warning</Badge>
                  )}
                </div>
                <div className="space-y-2">
                  {Object.entries(budgetAlerts.alerts_by_store || {}).map(([storeId, storeData]) => (
                    <div key={storeId} className="text-sm">
                      <span className="font-medium">{storeData.store_name}:</span>
                      <span className="ml-2 text-slate-600">
                        {storeData.alerts.map(a => 
                          a.type === 'purchase_over_budget' ? 'Purchases over budget' :
                          a.type === 'wages_over_budget' ? 'Wages over budget' :
                          'Sales below target'
                        ).join(', ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Completion Alert */}
      {summary && summary.data_completion.stores_with_data < summary.data_completion.total_stores && (
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="text-orange-600" size={24} />
            <div>
              <p className="font-medium text-orange-800">
                Data pending for {summary.data_completion.total_stores - summary.data_completion.stores_with_data} stores
              </p>
              <p className="text-sm text-orange-600">
                {summary.data_completion.stores_with_data} of {summary.data_completion.total_stores} stores have submitted financial data this week
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Stats Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Sales Card */}
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600 uppercase tracking-wide">Total Sales</p>
                  <p className="text-3xl font-bold text-green-800 mt-1">{formatCurrency(summary.totals.sales)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {summary.totals.sales_change > 0 ? (
                      <Badge className="bg-green-200 text-green-800">
                        <ArrowUpRight size={14} className="mr-1" />
                        {formatPercent(summary.totals.sales_change)}
                      </Badge>
                    ) : summary.totals.sales_change < 0 ? (
                      <Badge className="bg-red-200 text-red-800">
                        <ArrowDownRight size={14} className="mr-1" />
                        {formatPercent(summary.totals.sales_change)}
                      </Badge>
                    ) : (
                      <Badge variant="outline">No change</Badge>
                    )}
                    <span className="text-xs text-slate-500">vs last week</span>
                  </div>
                </div>
                <div className="p-3 bg-green-200 rounded-xl">
                  <DollarSign className="text-green-700" size={28} />
                </div>
              </div>
              {summary.budget_status.sales_target > 0 && (
                <div className="mt-4 pt-4 border-t border-green-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-green-600">Target</span>
                    <span className="font-medium text-green-800">{formatCurrency(summary.budget_status.sales_target)}</span>
                  </div>
                  <div className="mt-2 h-2 bg-green-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-600 rounded-full transition-all"
                      style={{ width: `${Math.min((summary.totals.sales / summary.budget_status.sales_target) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Purchases Card */}
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600 uppercase tracking-wide">Total Purchases</p>
                  <p className="text-3xl font-bold text-blue-800 mt-1">{formatCurrency(summary.totals.purchase)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={summary.totals.purchase_percentage > 35 ? 'bg-red-200 text-red-800' : 'bg-blue-200 text-blue-800'}>
                      {summary.totals.purchase_percentage}% of sales
                    </Badge>
                    {summary.totals.purchase_change !== 0 && (
                      <span className="text-xs text-slate-500">
                        {summary.totals.purchase_change > 0 ? '↑' : '↓'} {Math.abs(summary.totals.purchase_change)}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-3 bg-blue-200 rounded-xl">
                  <ShoppingCart className="text-blue-700" size={28} />
                </div>
              </div>
              {summary.budget_status.purchase_budget > 0 && (
                <div className="mt-4 pt-4 border-t border-blue-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-blue-600">Budget</span>
                    <span className="font-medium text-blue-800">{formatCurrency(summary.budget_status.purchase_budget)}</span>
                  </div>
                  <div className="mt-2 h-2 bg-blue-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        summary.totals.purchase > summary.budget_status.purchase_budget ? 'bg-red-500' : 'bg-blue-600'
                      }`}
                      style={{ width: `${Math.min((summary.totals.purchase / summary.budget_status.purchase_budget) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Wages Card */}
          <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600 uppercase tracking-wide">Total Wages</p>
                  <p className="text-3xl font-bold text-purple-800 mt-1">{formatCurrency(summary.totals.wages)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={summary.totals.wages_percentage > 25 ? 'bg-red-200 text-red-800' : 'bg-purple-200 text-purple-800'}>
                      {summary.totals.wages_percentage}% of sales
                    </Badge>
                    {summary.totals.wages_change !== 0 && (
                      <span className="text-xs text-slate-500">
                        {summary.totals.wages_change > 0 ? '↑' : '↓'} {Math.abs(summary.totals.wages_change)}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-3 bg-purple-200 rounded-xl">
                  <Users className="text-purple-700" size={28} />
                </div>
              </div>
              {summary.budget_status.wages_budget > 0 && (
                <div className="mt-4 pt-4 border-t border-purple-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-purple-600">Budget</span>
                    <span className="font-medium text-purple-800">{formatCurrency(summary.budget_status.wages_budget)}</span>
                  </div>
                  <div className="mt-2 h-2 bg-purple-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        summary.totals.wages > summary.budget_status.wages_budget ? 'bg-red-500' : 'bg-purple-600'
                      }`}
                      style={{ width: `${Math.min((summary.totals.wages / summary.budget_status.wages_budget) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Store Health Overview */}
      {summary && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <CheckCircle className="mx-auto text-green-600 mb-2" size={32} />
              <p className="text-3xl font-bold text-green-700">{summary.store_health.good}</p>
              <p className="text-sm text-green-600">Healthy Stores</p>
            </CardContent>
          </Card>
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="p-4 text-center">
              <AlertTriangle className="mx-auto text-yellow-600 mb-2" size={32} />
              <p className="text-3xl font-bold text-yellow-700">{summary.store_health.warning}</p>
              <p className="text-sm text-yellow-600">Need Attention</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4 text-center">
              <AlertCircle className="mx-auto text-red-600 mb-2" size={32} />
              <p className="text-3xl font-bold text-red-700">{summary.store_health.critical}</p>
              <p className="text-sm text-red-600">Critical</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Alerts Section */}
      {summary?.alerts?.length > 0 && (
        <Card className="border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-red-700">
              <AlertTriangle size={20} />
              Budget Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {summary.alerts.map((alert, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                  <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-red-800">{alert.store}:</span>
                    <span className="text-red-600 ml-2">{alert.message}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trends Chart */}
      {trends.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 size={20} className="text-slate-500" />
              8-Week Financial Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    labelStyle={{ fontWeight: 'bold' }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="sales" 
                    name="Sales"
                    stroke="#10b981" 
                    strokeWidth={3}
                    dot={{ fill: '#10b981', strokeWidth: 2 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="purchase" 
                    name="Purchases"
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="wages" 
                    name="Wages"
                    stroke="#8b5cf6" 
                    strokeWidth={2}
                    dot={{ fill: '#8b5cf6', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Store-by-Store Breakdown */}
      {weeklyReport && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building size={20} className="text-slate-500" />
              Store Breakdown - This Week
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Store</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">Sales</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">Purchases</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">%</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">Wages</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">%</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-600">Status</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {weeklyReport.stores.map(store => {
                    const healthStyle = HEALTH_COLORS[store.health];
                    return (
                      <tr key={store.store_id} className={`border-b hover:bg-slate-50 ${healthStyle.bg}`}>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-slate-800">{store.store_name}</p>
                            <p className="text-xs text-slate-500">{store.franchisee}</p>
                          </div>
                        </td>
                        <td className="text-right py-3 px-4 font-semibold text-green-700">
                          {store.has_data ? formatCurrency(store.sales.amount) : '-'}
                        </td>
                        <td className="text-right py-3 px-4 font-semibold text-blue-700">
                          {store.has_data ? formatCurrency(store.purchase.amount) : '-'}
                        </td>
                        <td className="text-right py-3 px-4">
                          {store.has_data && (
                            <Badge className={store.purchase.percentage_of_sales > 35 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}>
                              {store.purchase.percentage_of_sales}%
                            </Badge>
                          )}
                        </td>
                        <td className="text-right py-3 px-4 font-semibold text-purple-700">
                          {store.has_data ? formatCurrency(store.wages.amount) : '-'}
                        </td>
                        <td className="text-right py-3 px-4">
                          {store.has_data && (
                            <Badge className={store.wages.percentage_of_sales > 25 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}>
                              {store.wages.percentage_of_sales}%
                            </Badge>
                          )}
                        </td>
                        <td className="text-center py-3 px-4">
                          {store.has_data ? (
                            <Badge className={healthStyle.badge}>
                              {store.health === 'good' ? 'Healthy' : store.health === 'warning' ? 'Attention' : 'Critical'}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-400">No Data</Badge>
                          )}
                        </td>
                        <td className="text-center py-3 px-4">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => loadStoreHistory(store.store_id)}
                          >
                            <ChevronRight size={16} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 font-bold">
                    <td className="py-3 px-4 text-slate-800">TOTAL</td>
                    <td className="text-right py-3 px-4 text-green-700">{formatCurrency(weeklyReport.totals.sales)}</td>
                    <td className="text-right py-3 px-4 text-blue-700">{formatCurrency(weeklyReport.totals.purchase)}</td>
                    <td className="text-right py-3 px-4">
                      <Badge className="bg-slate-200 text-slate-700">{weeklyReport.totals.purchase_percentage}%</Badge>
                    </td>
                    <td className="text-right py-3 px-4 text-purple-700">{formatCurrency(weeklyReport.totals.wages)}</td>
                    <td className="text-right py-3 px-4">
                      <Badge className="bg-slate-200 text-slate-700">{weeklyReport.totals.wages_percentage}%</Badge>
                    </td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Store Detail Modal */}
      {selectedStore && storeHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{storeHistory.store.name} - Financial History</CardTitle>
                <p className="text-sm text-slate-500">{storeHistory.store.franchisee} • {storeHistory.store.location}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedStore(null)}>✕</Button>
            </CardHeader>
            <CardContent>
              {storeHistory.budget && (
                <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-500">Weekly Sales Target</p>
                    <p className="font-bold text-green-700">{formatCurrency(storeHistory.budget.weekly_sales_target)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Purchase Budget</p>
                    <p className="font-bold text-blue-700">{formatCurrency(storeHistory.budget.weekly_purchase_budget)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Wages Budget</p>
                    <p className="font-bold text-purple-700">{formatCurrency(storeHistory.budget.weekly_wages_budget)}</p>
                  </div>
                </div>
              )}
              
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-sm font-medium text-slate-600">Week Ending</th>
                    <th className="text-right py-2 text-sm font-medium text-slate-600">Sales</th>
                    <th className="text-right py-2 text-sm font-medium text-slate-600">Purchases</th>
                    <th className="text-right py-2 text-sm font-medium text-slate-600">Wages</th>
                  </tr>
                </thead>
                <tbody>
                  {storeHistory.history.map((h, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="py-2 text-sm">{new Date(h.week_ending).toLocaleDateString('en-AU')}</td>
                      <td className="text-right py-2 text-sm text-green-700 font-medium">{formatCurrency(h.sales_amount)}</td>
                      <td className="text-right py-2 text-sm text-blue-700 font-medium">{formatCurrency(h.purchase_amount)}</td>
                      <td className="text-right py-2 text-sm text-purple-700 font-medium">{formatCurrency(h.wages_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
